import {NoteTypeEnum} from "../../models/scorer/NoteTypeEnum";
import {emitPerformanceScoring_completed, subscribeToScorerEvents} from "../eventBus/scorer/scorerEventBus";
import {ScorerEventTypes} from "../eventBus/scorer/ScorerEventTypes";
import {IScoredNote, INoteScoreCriteria} from "../../models/scorer/scorerTypes";
import IPlayerNote from "../../models/scorer/IPlayerNote";
import {NoteScores} from "../../models/scorer/NoteScores";
import {NoteScoreReason} from "../../models/scorer/NoteScoreReason";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {IPerformanceScore} from "../../models/scorer/IPerformanceScore";
import {INotesScore} from "../../models/scorer/INotesScore";
import {logger} from "../log/logger";

interface INoteTypesValues {
  [NoteTypeEnum.whole]: number;
  [NoteTypeEnum.half]: number;
  [NoteTypeEnum.quarter]: number;
  [NoteTypeEnum.lessThanQuarter]: number;
}

export const noteWeights: INoteTypesValues = {
  [NoteTypeEnum.whole]: 1,
  [NoteTypeEnum.half]: .8,
  [NoteTypeEnum.quarter]: .6,
  [NoteTypeEnum.lessThanQuarter]: .5,
};

export const scorePortion = .95; //point multiplier modifier

export const difficultyScoreBaselines = {
  BEGINNER_EASY: 80000,
  BEGINNER_MEDIUM: 90000,
  BEGINNER_HARD: 100000,
  INTER_EASY:125000,
  INTER_MEDIUM: 150000,
  INTER_HARD: 175000,
  ADVANCE_EASY: 200000,
  ADVANCE_MEDIUM: 225000,
  ADVANCE_HARD: 250000,
};

/**
 * Listens for notesScoringCompleted event so it can tally the correct, incorrect, etc notes and determine a final set of scores, based on difficulty, mode, note counts, etc.
 * Emits performanceScoringCompleted event once calculated, which is listened to by the lesson service, which then goes on to save a practice record and display the score modal.
 */
class PerformanceScorer{
  unsubscribeFuncs: (() => void)[];
  constructor() {
    this.unsubscribeFuncs = [
      subscribeToScorerEvents(this.handleScorerEvent.bind(this)),
    ];
  }
  handleScorerEvent(e: ScorerEventTypes){
    switch(e.type){
      case "notesScoringCompleted":
        handleNotesScoringCompleted(e.notesScore, noteWeights, scorePortion);
        break;
    }
  }
}

/**
 * When note scoring is completed (typically triggered by song ended event from soundslice), we want to evaluate all the note scores, tally missed, incorrect, correct, etc, and determine scores based on difficulty, note counts, etc.
 * @param notesScore - result from noteScoringCompleted event.
 * @param noteWeights - config for weight values for each note type
 * @param scorePortion - config for point multiplier.
 */
function handleNotesScoringCompleted(notesScore: INotesScore, noteWeights: INoteTypesValues, scorePortion: number){
  if(notesScore.seekOffsetSeconds > 0 || notesScore.loopEndOffsetSeconds > -1  ){  return logger.lessonScorerPerformance.warn({message: 'performance scoring should only take place when we play from the beginning until the end'}); }
  if(notesScore.unscoredNotesCriteriaInSelection.length > 0) { return logger.lessonScorerPerformance.warn({message: 'note score completed but there are unscored notes, so performance score will not be calculated.' + notesScore}); }
  // console.log(`note scoring: `, JSON.stringify(notesScore, null, 2));  <-- don't do this or you'll get a circular structure error do to how we score tie notes.
  const performanceScore = createPerformanceScore(notesScore, noteWeights, scorePortion);
  emitPerformanceScoring_completed(performanceScore); //emit the event so lesson service can save practice record and show the modal.
}

/**
 * Calculates the score and creates the performance score result that is sent as part of the performanceScoringCompleted event.
 * @param notesScore - result from noteScoringCompleted event.
 * @param noteWeights - config for weight values for each note type
 * @param scorePortion - config for point multiplier.
 */
export function createPerformanceScore(notesScore:INotesScore, noteWeights: INoteTypesValues, scorePortion: number): IPerformanceScore{
  const {scoredNotes, unscoredNotesCriteriaInSelection, unscoredPlayerNotes, speed, segmentMode, hands, lessonId, segmentId, lessonDifficulty, } = notesScore;
  const scores = calculateScores(scoredNotes, unscoredNotesCriteriaInSelection, unscoredPlayerNotes, speed, segmentMode, noteWeights, scorePortion, lessonDifficulty);
  const scoringDurationMs = (notesScore.scoringEndTime - notesScore.scoringStartTime);
  const date = Math.round((Date.now() - scoringDurationMs) / 1000);
  return { lessonId, segmentId, segmentMode, hands, scoringDurationMs, date, ...scores};
}

/**
 * Score calculation is an implementation of what is in the legacy app.
 * The legacy app was incomplete in scoring, and many features were partially implemented, abandoned, but then never removed.
 * @param scoredNotes  array of scored notes.
 * @param unscoredNotesCriteriaInSegment - array of unscored criteria (should always be empty)
 * @param unscoredPlayerNotes - array of unscored player notes (should always be empty)
 * @param speed - playback speed used when scoring was done.  This is needed to send a tempo of .5 or 1 when _not_ in performance mode.
 * @param segmentMode - When mode is performance, the totalPerformanceScore is calculated, which is part of the practice record.
 * @param noteWeights - config for note point weights
 * @param scorePortion - config for point multiplier
 * @param lessonDifficulty - is used when calculating note point values and stars score.
 */
export function calculateScores(scoredNotes: IScoredNote[], unscoredNotesCriteriaInSegment: INoteScoreCriteria[], unscoredPlayerNotes: IPlayerNote[], speed: number, segmentMode: SegmentModeEnum, noteWeights: INoteTypesValues, scorePortion: number, lessonDifficulty: number){
  const noteTypeCounts = getTypeNoteCounts(scoredNotes);
  const weightedNotePointMultiplier = calculateWeightedNotePointMultiplier(noteTypeCounts, noteWeights, scorePortion, lessonDifficulty);
  const correctNotesPoints = calculateTotalPointsForCorrectNotes(scoredNotes, weightedNotePointMultiplier, noteWeights);
  const incorrectNotesPoints = calculateTotalPointsForIncorrectNotes(scoredNotes, weightedNotePointMultiplier);
  const songScore = Math.max(correctNotesPoints - incorrectNotesPoints, 0);

  const totalNumberOfNotesInSegment = noteTypeCounts[NoteTypeEnum.whole] + noteTypeCounts[NoteTypeEnum.half] + noteTypeCounts[NoteTypeEnum.quarter] + noteTypeCounts[NoteTypeEnum.lessThanQuarter]; //totalNotes

  const totalNumberOfNotesPlayedWhereRhythmWasAccurate = scoredNotes.filter(n => n.score === NoteScores.correct).length; //rhythmAccNotes
  const totalNumberOfNotesPlayedWhereRhythmWasInaccurate = scoredNotes.filter(n => n.score === NoteScores.close).length; //rhythmInAccNotes
  const totalMissedNotes = scoredNotes.filter(n => n.score === NoteScores.incorrect && n.noteScoreReason === NoteScoreReason.IncorrectBecauseItWasNotPlayed).length;
  const totalWrongNotes = scoredNotes.filter(n => n.score === NoteScores.incorrect && n.noteScoreReason === NoteScoreReason.IncorrectBecauseItWasExtraneous).length;
  const starsScore = calculateStarsScore(songScore, lessonDifficulty);

  const totalNotesPlayed = scoredNotes.filter(n => n.noteScoreReason !== NoteScoreReason.IncorrectBecauseItWasNotPlayed).length;
  let scorePercentage = (totalNumberOfNotesPlayedWhereRhythmWasAccurate / (totalNotesPlayed + totalMissedNotes)) * 100;
  scorePercentage = isNaN(scorePercentage) ? 0 : scorePercentage;
  const roundedScorePercentage = Math.round(scorePercentage);

  //mimic legacy's bad logic
  let tempo = speed === 1 ? 1 : .5;
  let totalPerformanceScore = 0; //mimic legacy.  legacy add bonus score, but bonus score is always 0

  if(segmentMode === SegmentModeEnum.perform){
    totalPerformanceScore = songScore / 2500;
    logger.lessonScorerPerformance.log({message: `total performance score is ` + totalPerformanceScore });
    tempo = 1; //mimic legacy
  }

  debugNumbers(scoredNotes, unscoredNotesCriteriaInSegment, unscoredPlayerNotes, noteTypeCounts, weightedNotePointMultiplier, correctNotesPoints, incorrectNotesPoints, totalNumberOfNotesInSegment, segmentMode);
  return {totalNumberOfNotesInSegment, totalNotesPlayed, totalNumberOfNotesPlayedWhereRhythmWasAccurate, totalNumberOfNotesPlayedWhereRhythmWasInaccurate, totalMissedNotes, totalWrongNotes, starsScore, roundedScorePercentage, tempo, totalPerformanceScore, songScore, };
}

/**
 * Determine how many stars the user gets, based on their song score (number of correct - incorrect)
 * @param songScore - correct note points - incorrect note points
 * @param lessonDifficulty - used to get baseline difficulty levels. e.g. beginner easy is 80,000
 */
function calculateStarsScore(songScore: number, lessonDifficulty: number){
  const difficultyPoints = getDifficultyPoints(lessonDifficulty);
  if(songScore < .2 * difficultyPoints) {
    return 0;
  } else if(songScore < .4 * difficultyPoints){
    return 1;
  } else if(songScore < .6 * difficultyPoints){
    return 2;
  } else if(songScore < .8 * difficultyPoints){
    return 3;
  } else if(songScore < .9 * difficultyPoints){
    return 4;
  } else {
    return 5;
  }
}

/**
 * Song points for correct notes.
 * @param scoredNotes - array of scored notes
 * @param weightedNotePointMultiplier - point multiplier based on number of whole notes, lesson difficulty, etc.
 * @param noteWeights - config of point weights for each note type.
 */
export function calculateTotalPointsForCorrectNotes(scoredNotes: IScoredNote[], weightedNotePointMultiplier: number, noteWeights: INoteTypesValues){
  if(scoredNotes.length <= 0){ return 0; }
  let currentBarIndex = scoredNotes[0].barIndex;
  let measuresStreakedSinceLastBadNote = 0;
  let currentMeasureStreak = true;
  return scoredNotes.reduce((total, scoredNote) => {
    //determine how many measures have been streaked so we can come up with a note point streak multiplier.  this code is from legacy.
    if(scoredNote.score === NoteScores.correct){
      const noteIsInTheSameBarAsPreviousNote = scoredNote.barIndex === currentBarIndex;
      if(currentMeasureStreak && !noteIsInTheSameBarAsPreviousNote){
        measuresStreakedSinceLastBadNote++;
      }
      currentMeasureStreak = true;
    }else{
      measuresStreakedSinceLastBadNote = 0;
      currentMeasureStreak = false;
    }

    currentBarIndex = scoredNote.barIndex;
    const pointsForNote = calculatePointsForCorrectNote(scoredNote, weightedNotePointMultiplier, noteWeights, measuresStreakedSinceLastBadNote);
    return total + pointsForNote;
  }, 0);
}

/**
 * When a user plays correct notes for measures in a row, the points awarded for the note increases by this streak multiplier, which ranges from 1 to 1.1
 * @param measuresStreakedSinceLastBadNote
 */
export function calculateNotePointStreakMultiplier(measuresStreakedSinceLastBadNote: number){
  if(measuresStreakedSinceLastBadNote < 1){ return 1; }
  if(measuresStreakedSinceLastBadNote > 10){ return 1.1; }
  return 1 + (measuresStreakedSinceLastBadNote / 100);
}

/**
 * Song points to be deducted for incorrect notes.
 * @param scoredNotes - array of scored notes
 * @param weightedNotePointMultiplier - point multiplier based on number of whole notes, lesson difficulty, etc.
 */
export function calculateTotalPointsForIncorrectNotes(scoredNotes: IScoredNote[], weightedNotePointMultiplier: number){
  const totalIncorrectNotes = getNumberOfIncorrectNotes(scoredNotes);
  return totalIncorrectNotes * weightedNotePointMultiplier;
}

/**
 * Only gives points for correct notes.
 * @param scoredNote - single note result from note scorer
 * @param weightedNotePointMultiplier - point multiplier based on number of whole notes, lesson difficulty, etc.
 * @param noteWeights - config of point weights for each note type.
 * @param measuresStreakedSinceLastBadNote - how many measures have been played correctly since the last bad note was played.  used to calculate the streak multiplier.
 */
export function calculatePointsForCorrectNote(scoredNote: IScoredNote, weightedNotePointMultiplier: number, noteWeights: INoteTypesValues, measuresStreakedSinceLastBadNote: number){
  //only correct notes get points
  if(scoredNote.score !== NoteScores.correct){ return 0; }
  if(scoredNote.noteScoreCriteria === undefined) { logger.lessonScorerPerformance.error({message: 'note was marked correct but no score criteria included.'}); return 0; }
  const noteAccuracy = 1; //mimic legacy
  const currentTempo = 1; //mimic legacy
  const streakMultiplier = calculateNotePointStreakMultiplier(measuresStreakedSinceLastBadNote);
  const points = weightedNotePointMultiplier * noteWeights[scoredNote.noteScoreCriteria.noteType] * streakMultiplier * noteAccuracy * currentTempo;
  logger.lessonScorerPerformance.log({message: `note score: ${scoredNote.noteScoreCriteria.noteType} multiplier: ${weightedNotePointMultiplier} val: ${noteWeights[scoredNote.noteScoreCriteria.noteType]} accuracy: ${noteAccuracy} streak: ${streakMultiplier} temp: ${currentTempo}`});
  return points;
}

/**
 * incorrect pitch evaluation and extraneous count as wrong/incorrect
 * @param scoredNotes - array of scored notes
 */
export function getNumberOfIncorrectNotes(scoredNotes: IScoredNote[]){
  return scoredNotes.reduce((total, scoredNote) => {
    if(scoredNote.score === NoteScores.incorrect){ total++; }
    return total;
  }, 0);
}

/**
 * The point multiplier is used when calculating correct & incorrect note points.
 * It is calculated using the lesson difficulty, note weights, and note type counts.
 * @param noteTypeCounts - count of notes in the segment, broken down by each note type. e.g. wholeNotes: 19, halfNotes: 12,
 * @param noteWeights - config of point weights for each note type.
 * @param scorePortion - config for point multiplier
 * @param lessonDifficulty - used to get baseline difficulty levels. e.g. beginner easy is 80,000
 */
export function calculateWeightedNotePointMultiplier(noteTypeCounts: INoteTypesValues, noteWeights: INoteTypesValues, scorePortion: number, lessonDifficulty: number){
  const difficultyPoints = getDifficultyPoints(lessonDifficulty);
  const difficultyWeight = difficultyPoints * scorePortion;
  const wholeNoteWeight = noteWeights[NoteTypeEnum.whole] * noteTypeCounts[NoteTypeEnum.whole];
  const halfNoteWeight = noteWeights[NoteTypeEnum.half] * noteTypeCounts[NoteTypeEnum.half];
  const quarterNoteWeight = noteWeights[NoteTypeEnum.quarter] * noteTypeCounts[NoteTypeEnum.quarter];
  const underQuarterNoteWeight = noteWeights[NoteTypeEnum.lessThanQuarter] * noteTypeCounts[NoteTypeEnum.lessThanQuarter];
  const totalNoteWeight = wholeNoteWeight + halfNoteWeight + quarterNoteWeight + underQuarterNoteWeight;
  const totalWeightedMultiplier = totalNoteWeight !== 0 ? difficultyWeight / totalNoteWeight : 0;
  logger.lessonScorerPerformance.log({ message: `totalNoteWeight: ${totalNoteWeight}  wholeNote: ${wholeNoteWeight}  half: ${halfNoteWeight} quarter: ${quarterNoteWeight}  under: ${underQuarterNoteWeight}  difficultyWeight: ${difficultyWeight} scorePortion: ${scorePortion}`});
  return totalWeightedMultiplier;
}

/**
 * Baseline points based on lesson difficulty.
 * @param lessonDifficulty - difficulty of the lesson. 1-9
 */
export function getDifficultyPoints(lessonDifficulty: number){
  switch(lessonDifficulty){
    case 1:
      return difficultyScoreBaselines.BEGINNER_EASY;
    case 2:
      return difficultyScoreBaselines.BEGINNER_MEDIUM;
    case 3:
      return difficultyScoreBaselines.BEGINNER_HARD;
    case 4:
      return difficultyScoreBaselines.INTER_EASY;
    case 5:
      return difficultyScoreBaselines.INTER_MEDIUM;
    case 6:
      return difficultyScoreBaselines.INTER_HARD;
    case 7:
      return difficultyScoreBaselines.ADVANCE_EASY;
    case 8:
      return difficultyScoreBaselines.ADVANCE_MEDIUM;
    case 9:
      return difficultyScoreBaselines.ADVANCE_HARD;
    default:
      return 1;
  }
}

/**
 * Group counts of note types in the segment by looking at the scoredNotes array, which will only have noteType defined for notes found in the segment (not extraneous ones)
 * @param scoredNotes - array of scored notes.
 */
export function getTypeNoteCounts(scoredNotes: IScoredNote[]): INoteTypesValues{
  return scoredNotes.reduce((agg, note)=>{
    if(note.noteScoreCriteria && note.noteScoreCriteria.noteType !== undefined){
      agg[note.noteScoreCriteria.noteType]++;
    }
    return agg;
  }, {[NoteTypeEnum.whole]: 0, [NoteTypeEnum.half]: 0, [NoteTypeEnum.quarter]: 0, [NoteTypeEnum.lessThanQuarter]: 0});
}

function debugNumbers(scoredNotes: IScoredNote[], unscoredNotesCriteriaInSegment: INoteScoreCriteria[], unscoredPlayerNotes: IPlayerNote[], noteTypeCounts: INoteTypesValues, weightedNotePointMultiplier: number, correctNotesPoints: number, incorrectNotesPoints: number, totalNumberOfNotesInSegment: number, segmentMode: string,){
  logger.lessonScorerPerformance.debug({
    message: `debug numbers`,
    data: [
      {key: 'scoredNotes', value: scoredNotes},
      {key: 'unscoredNotesCriteriaInSegment', value: unscoredNotesCriteriaInSegment},
      {key: 'unscoredPlayerNotes', value: unscoredPlayerNotes},
      {key: 'noteTypeCounts', value: noteTypeCounts},
      {key: 'weightedNotePointMultiplier', value: weightedNotePointMultiplier},
      {key: 'correctNotesPoints', value: correctNotesPoints},
      {key: 'incorrectNotesPoints', value: incorrectNotesPoints},
      {key: 'totalNumberOfNotesInSegment', value: totalNumberOfNotesInSegment},
      {key: 'segmentMode', value: segmentMode},
    ]
  });
}

const performanceScorer = new PerformanceScorer();
export default performanceScorer;